import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const currentMonthName = () => {
  return new Date().toLocaleString('en-GB', { month: 'long', year: 'numeric' });
};

export default function ReportForm({ clients, onGenerate }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1 — client + report name
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientData, setClientData] = useState(null);
  const [reportName, setReportName] = useState(`${currentMonthName()} Website and SEO Report`);

  // Step 2 — keyword performance
  const [keywords, setKeywords] = useState({ top3: '', top10: '', top20: '', top20plus: '' });

  // Step 3 — traffic
  const [traffic, setTraffic] = useState({ sessions: '', users: '', pageviews: '', avgDuration: '' });

  // Step 4 — conversions
  const [conversions, setConversions] = useState({ calls: '', emails: '', forms: '', other: '', otherLabel: '' });

  // Step 5 — actions completed
  const [actions, setActions] = useState({
    technical: [''],
    content: [''],
    backlinks: [''],
    citations: [''],
  });

  // Step 6 — commentary + next steps
  const [commentary, setCommentary] = useState({
    newIdeas: '',
    highlightedRankings: '',
    plannedContent: '',
    trafficCommentary: '',
    summary: '',
  });

  // Baseline data (from client profile or entered here)
  const [baseline, setBaseline] = useState({ top3: '', top10: '', top20: '', top20plus: '', traffic: '', calls: '', emails: '', forms: '', date: '' });
  const [baselineSource, setBaselineSource] = useState('client'); // 'client' | 'manual'

  useEffect(() => {
    if (clientId) {
      const c = clients.find(c => c.id === clientId);
      if (c) {
        setClientData(c);
        setClientName(c.name);
        // Load baseline if stored
        if (c.baseline_top3 !== null) {
          setBaseline({
            top3: c.baseline_top3 ?? '',
            top10: c.baseline_top10 ?? '',
            top20: c.baseline_top20 ?? '',
            top20plus: c.baseline_top20plus ?? '',
            traffic: c.baseline_traffic ?? '',
            calls: c.baseline_calls ?? '',
            emails: c.baseline_emails ?? '',
            forms: c.baseline_forms ?? '',
            date: c.baseline_date ?? '',
          });
          setBaselineSource('client');
        } else {
          setBaselineSource('manual');
        }
      }
    }
  }, [clientId]);

  const saveBaseline = async () => {
    if (!clientId) return;
    setSaving(true);
    await supabase.from('clients').update({
      baseline_top3: parseInt(baseline.top3) || 0,
      baseline_top10: parseInt(baseline.top10) || 0,
      baseline_top20: parseInt(baseline.top20) || 0,
      baseline_top20plus: parseInt(baseline.top20plus) || 0,
      baseline_traffic: parseInt(baseline.traffic) || 0,
      baseline_calls: parseInt(baseline.calls) || 0,
      baseline_emails: parseInt(baseline.emails) || 0,
      baseline_forms: parseInt(baseline.forms) || 0,
      baseline_date: baseline.date || null,
    }).eq('id', clientId);
    setSaving(false);
    setBaselineSource('client');
  };

  const updateAction = (section, index, value) => {
    setActions(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => i === index ? value : item)
    }));
  };

  const addAction = (section) => setActions(prev => ({ ...prev, [section]: [...prev[section], ''] }));
  const removeAction = (section, index) => setActions(prev => ({ ...prev, [section]: prev[section].filter((_, i) => i !== index) }));

  const handleGenerate = () => {
    onGenerate({
      clientId, clientName, clientData, reportName,
      keywords, traffic, conversions, actions, commentary, baseline,
    });
  };

  const stepLabels = ['Client & report', 'Keywords', 'Traffic', 'Conversions', 'Actions', 'Commentary'];
  const totalSteps = 6;

  const StepIndicator = () => (
    <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', alignItems: 'center', overflowX: 'auto', paddingBottom: 4 }}>
      {stepLabels.map((label, i) => {
        const n = i + 1; const isDone = step > n; const isActive = step === n;
        return (
          <React.Fragment key={n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <div onClick={() => isDone && setStep(n)} style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, cursor: isDone ? 'pointer' : 'default', background: isDone ? '#3B6D11' : isActive ? '#185FA5' : 'var(--gray-200)', color: isDone || isActive ? '#fff' : 'var(--gray-500)', flexShrink: 0 }}>
                {isDone ? '✓' : n}
              </div>
              <span style={{ fontSize: 12, color: isActive ? 'var(--gray-900)' : 'var(--gray-400)', fontWeight: isActive ? 600 : 400, whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {i < totalSteps - 1 && <div style={{ flex: 1, height: 1, background: 'var(--gray-200)', margin: '0 8px', minWidth: 16 }} />}
          </React.Fragment>
        );
      })}
    </div>
  );

  // ── Step 1 ──────────────────────────────────────────────────
  if (step === 1) return (
    <div>
      <StepIndicator />
      <div className="card">
        <div className="card-title">Client & report name</div>
        <div className="form-group">
          <label>Select client</label>
          <select value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">— Select a client —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {!clientId && (
          <div className="form-group">
            <label>Or enter client name manually</label>
            <input type="text" placeholder="e.g. Carpet Bright UK" value={clientName} onChange={e => setClientName(e.target.value)} />
          </div>
        )}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Report name</label>
          <input type="text" value={reportName} onChange={e => setReportName(e.target.value)} />
        </div>
      </div>

      {/* Baseline data */}
      <div className="card">
        <div className="card-title">Day 1 baseline data</div>
        <div className="card-sub">
          {baselineSource === 'client' && clientId
            ? 'Pre-filled from client profile. Update if needed.'
            : 'Enter the starting position before SEO work began. This will be saved to the client profile.'}
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Keyword rankings on day 1</div>
        <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
          {[['top3', 'Top 3'], ['top10', 'Top 10'], ['top20', 'Top 20'], ['top20plus', '20+']].map(([key, label]) => (
            <div key={key}>
              <label>{label}</label>
              <input type="number" min="0" placeholder="0" value={baseline[key]} onChange={e => setBaseline(p => ({ ...p, [key]: e.target.value }))} />
            </div>
          ))}
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Traffic & conversions on day 1</div>
        <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
          {[['traffic', 'Monthly sessions'], ['calls', 'Phone calls'], ['emails', 'Emails'], ['forms', 'Form submissions']].map(([key, label]) => (
            <div key={key}>
              <label>{label}</label>
              <input type="number" min="0" placeholder="0" value={baseline[key]} onChange={e => setBaseline(p => ({ ...p, [key]: e.target.value }))} />
            </div>
          ))}
        </div>

        <div className="form-row form-row-2 form-group">
          <div>
            <label>Date SEO work started</label>
            <input type="date" value={baseline.date} onChange={e => setBaseline(p => ({ ...p, date: e.target.value }))} />
          </div>
          {clientId && (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="btn btn-sm" onClick={saveBaseline} disabled={saving}>
                {saving ? <><span className="spinner" /> Saving…</> : '💾 Save to client profile'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!clientName.trim()}>
          Next: Keyword performance →
        </button>
      </div>
    </div>
  );

  // ── Step 2 ──────────────────────────────────────────────────
  if (step === 2) return (
    <div>
      <StepIndicator />
      <div className="card">
        <div className="card-title">Keyword performance</div>
        <div className="card-sub">Enter the number of keywords ranking in each position band this month.</div>
        <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          {[['top3', 'Top 3 positions', '#2D6A21', '#EAF3DE'], ['top10', 'Top 10 (page 1)', '#185FA5', '#E6F1FB'], ['top20', 'Top 20 (page 2)', '#633806', '#FAEEDA'], ['top20plus', 'Position 20+', '#444441', '#F1EFE8']].map(([key, label, color, bg]) => (
            <div key={key} style={{ background: bg, borderRadius: 'var(--radius-lg)', padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: 12, color, fontWeight: 600, marginBottom: 8 }}>{label}</div>
              <input type="number" min="0" placeholder="0" value={keywords[key]} onChange={e => setKeywords(p => ({ ...p, [key]: e.target.value }))} style={{ textAlign: 'center', fontSize: 24, fontWeight: 700, border: 'none', background: 'transparent', width: '100%', color }} />
              {baseline[key] !== '' && baseline[key] !== undefined && (
                <div style={{ fontSize: 11, color, opacity: 0.7, marginTop: 4 }}>Day 1: {baseline[key]}</div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={() => setStep(1)}>← Back</button>
        <button className="btn btn-primary" onClick={() => setStep(3)}>Next: Traffic →</button>
      </div>
    </div>
  );

  // ── Step 3 ──────────────────────────────────────────────────
  if (step === 3) return (
    <div>
      <StepIndicator />
      <div className="card">
        <div className="card-title">Traffic performance</div>
        <div className="card-sub">Enter data from Google Analytics for this month.</div>
        <div className="form-row form-row-2 form-group">
          <div>
            <label>Sessions / visits</label>
            <input type="number" min="0" placeholder="0" value={traffic.sessions} onChange={e => setTraffic(p => ({ ...p, sessions: e.target.value }))} />
          </div>
          <div>
            <label>Users</label>
            <input type="number" min="0" placeholder="0" value={traffic.users} onChange={e => setTraffic(p => ({ ...p, users: e.target.value }))} />
          </div>
        </div>
        <div className="form-row form-row-2 form-group">
          <div>
            <label>Page views</label>
            <input type="number" min="0" placeholder="0" value={traffic.pageviews} onChange={e => setTraffic(p => ({ ...p, pageviews: e.target.value }))} />
          </div>
          <div>
            <label>Avg. session duration</label>
            <input type="text" placeholder="e.g. 2m 34s" value={traffic.avgDuration} onChange={e => setTraffic(p => ({ ...p, avgDuration: e.target.value }))} />
          </div>
        </div>
        {baseline.traffic && (
          <div className="alert alert-info">Day 1 baseline: {baseline.traffic} sessions</div>
        )}
      </div>
      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={() => setStep(2)}>← Back</button>
        <button className="btn btn-primary" onClick={() => setStep(4)}>Next: Conversions →</button>
      </div>
    </div>
  );

  // ── Step 4 ──────────────────────────────────────────────────
  if (step === 4) return (
    <div>
      <StepIndicator />
      <div className="card">
        <div className="card-title">Conversion performance</div>
        <div className="card-sub">Enter leads and enquiries generated this month from all sources.</div>

        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          <strong>Reminder:</strong> Have you checked Google Business Profile for call/direction conversions? Are there any contact forms not tracked in GA?
        </div>

        <div className="form-row form-row-2 form-group">
          <div>
            <label>Phone calls</label>
            <input type="number" min="0" placeholder="0" value={conversions.calls} onChange={e => setConversions(p => ({ ...p, calls: e.target.value }))} />
          </div>
          <div>
            <label>Email enquiries</label>
            <input type="number" min="0" placeholder="0" value={conversions.emails} onChange={e => setConversions(p => ({ ...p, emails: e.target.value }))} />
          </div>
        </div>
        <div className="form-row form-row-2 form-group">
          <div>
            <label>Contact form submissions</label>
            <input type="number" min="0" placeholder="0" value={conversions.forms} onChange={e => setConversions(p => ({ ...p, forms: e.target.value }))} />
          </div>
          <div>
            <label>Other conversions</label>
            <input type="number" min="0" placeholder="0" value={conversions.other} onChange={e => setConversions(p => ({ ...p, other: e.target.value }))} />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Label for "other" conversions (optional)</label>
          <input type="text" placeholder="e.g. WhatsApp messages, live chat, GBP clicks" value={conversions.otherLabel} onChange={e => setConversions(p => ({ ...p, otherLabel: e.target.value }))} />
        </div>

        {(baseline.calls || baseline.emails || baseline.forms) && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--gray-500)' }}>
            Day 1 baseline — Calls: {baseline.calls || 0} · Emails: {baseline.emails || 0} · Forms: {baseline.forms || 0}
          </div>
        )}
      </div>
      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={() => setStep(3)}>← Back</button>
        <button className="btn btn-primary" onClick={() => setStep(5)}>Next: Actions completed →</button>
      </div>
    </div>
  );

  // ── Step 5 ──────────────────────────────────────────────────
  if (step === 5) {
    const sections = [
      { key: 'technical', label: 'Technical SEO actions', placeholder: 'e.g. Fixed broken links on /services page' },
      { key: 'content', label: 'Content produced', placeholder: 'e.g. Written and published carpet cleaning Bromley page' },
      { key: 'backlinks', label: 'Backlinks obtained', placeholder: 'e.g. Link acquired from bromleylocal.co.uk (DA 28)' },
      { key: 'citations', label: 'Citations obtained', placeholder: 'e.g. Added to Yell.com business directory' },
    ];
    return (
      <div>
        <StepIndicator />
        {sections.map(({ key, label, placeholder }) => (
          <div className="card" key={key} style={{ marginBottom: 12 }}>
            <div className="card-title">{label}</div>
            {actions[key].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input type="text" placeholder={placeholder} value={item} onChange={e => updateAction(key, i, e.target.value)} style={{ flex: 1 }} />
                {actions[key].length > 1 && (
                  <button className="btn btn-sm" onClick={() => removeAction(key, i)} style={{ color: 'var(--gray-400)', borderColor: 'transparent' }}>✕</button>
                )}
              </div>
            ))}
            <button className="btn btn-sm" onClick={() => addAction(key)}>+ Add</button>
          </div>
        ))}
        <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
          <button className="btn" onClick={() => setStep(4)}>← Back</button>
          <button className="btn btn-primary" onClick={() => setStep(6)}>Next: Commentary →</button>
        </div>
      </div>
    );
  }

  // ── Step 6 ──────────────────────────────────────────────────
  if (step === 6) return (
    <div>
      <StepIndicator />
      <div className="card">
        <div className="card-title">Commentary & next steps</div>
        <div className="card-sub">This information will be used by Claude to write the executive summary. All fields are optional.</div>

        <div className="form-group">
          <label>Highlighted ranking positions</label>
          <textarea rows={2} placeholder="e.g. Now ranking #1 for 'carpet cleaning bromley', #3 for 'carpet cleaner beckenham'" value={commentary.highlightedRankings} onChange={e => setCommentary(p => ({ ...p, highlightedRankings: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>Commentary on website traffic</label>
          <textarea rows={2} placeholder="e.g. Traffic up 34% month-on-month, driven by new location pages" value={commentary.trafficCommentary} onChange={e => setCommentary(p => ({ ...p, trafficCommentary: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>Planned content for next month</label>
          <textarea rows={2} placeholder="e.g. Writing pages for Croydon and Lewisham, updating homepage meta data" value={commentary.plannedContent} onChange={e => setCommentary(p => ({ ...p, plannedContent: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>New ideas (optional — leave blank if none)</label>
          <textarea rows={2} placeholder="e.g. Suggest adding a before/after gallery to the homepage" value={commentary.newIdeas} onChange={e => setCommentary(p => ({ ...p, newIdeas: e.target.value }))} />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Additional summary notes</label>
          <textarea rows={3} placeholder="Any other points to include in the executive summary" value={commentary.summary} onChange={e => setCommentary(p => ({ ...p, summary: e.target.value }))} />
        </div>
      </div>
      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={() => setStep(5)}>← Back</button>
        <button className="btn btn-primary" onClick={handleGenerate}>Generate report →</button>
      </div>
    </div>
  );

  return null;
}
